<template>
  <div class="content-viewer">
    <Teleport :to="fancyboxDialogRef" v-if="fancyboxDialogRef">
      <CommentModal ref="commentModalRef" @comment-added="refreshToolbarState()"/>
    </Teleport>
  </div>
</template>

<style lang="scss">
// Unscoped — these target Fancybox DOM which is outside Vue's component tree.
// Fancybox .f-button is 40x40, flex-centered. We extend it to show a count badge.
.f-button[data-social-action] {
  // Allow the button to grow for the count text
  width: auto;
  gap: 2px;
  padding: 0 8px;

  .like-count, .comment-count {
    font-size: 13px;
    min-width: 0;
  }
}

.f-button[data-social-action="like"] {
  .like-heart svg {
    transition: fill 0.15s, stroke 0.15s;
  }

  &.is-liked .like-heart svg {
    fill: #e74c3c;
    stroke: #e74c3c;
  }
}
</style>

<script lang="ts" setup>
import { computed, onUnmounted, ref, type ShallowRef, shallowRef } from 'vue'
import { Content } from '@/rd/ResearchDataModel'
import { Fancybox, type FancyboxInstance } from '@fancyapps/ui'
import type { CarouselInstance } from '@fancyapps/ui/dist/carousel/carousel'
import '@fancyapps/ui/dist/fancybox/fancybox.css'
import '@fancyapps/ui/dist/carousel/carousel.css'
import '@fancyapps/ui/dist/carousel/carousel.thumbs.css'
import { useCommentStore } from '@/rd/CommentStore'
import CommentModal from '@/explore/CommentModal.vue'

// Inline feather-icon SVGs — Fancybox toolbar uses static HTML templates, not Vue components.
// These are the same paths as HeartIcon and MessageCircleIcon from @zhuowenli/vue-feather-icons.
const HEART_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>`

const COMMENT_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>`

const commentStore = useCommentStore()

const activeContentItems = ref<Content[]>([])
const currentIndexRef = ref(0)
const commentModalRef = ref<InstanceType<typeof CommentModal>>()
const fancyboxDialogRef: ShallowRef<HTMLElement | null> = shallowRef(null)

const currentContentId = computed(() => activeContentItems.value[currentIndexRef.value]?.id)
const likedByMe = new Set<string>()

defineExpose({
  open,
  close,
})

onUnmounted(() => {
  Fancybox.destroy()
})

function open(contentItems: Content[], index: number) {
  const filtered = contentItems.filter(c => c.originalUrl && c.thumbUrl)
  activeContentItems.value = filtered

  // Adjust index for items removed by filtering
  const targetId = contentItems[index]?.id
  const filteredIndex = targetId ? filtered.findIndex(c => c.id === targetId) : -1
  currentIndexRef.value = Math.max(0, filteredIndex)

  // const contentIds = filtered.map(c => c.id).filter((id): id is string => !!id)
  // commentStore.ensureSeeded(contentIds) // TODO: remove when real social data is available

  const items = filtered.map(c => ({
    src: c.originalUrl,
    thumbSrc: c.thumbUrl,
    caption: c.caption,
  }))

  Fancybox.show(items, {
    startIndex: currentIndexRef.value,
    on: {
      ready: (fancybox) => {
        // Capture the <dialog> element so CommentModal can teleport into it
        const container = fancybox.getContainer()
        fancyboxDialogRef.value = container.closest('dialog') ?? container
        updateToolbarState(fancybox)
      },
      'Carousel.change': (fancybox, _carousel, newIndex) => {
        currentIndexRef.value = newIndex
        updateToolbarState(fancybox)
      },
      close: () => {
        activeContentItems.value = []
        fancyboxDialogRef.value = null
      },
    },
    Carousel: {
      Toolbar: {
        display: {
          left: ["counter"],
          middle: ["zoomIn", "zoomOut", "toggle1to1", "rotateCCW", "rotateCW"], //, "flipX", "flipY"],
          right: ["autoplay", "download", "fullscreen", "close"],
        },
        items: {
          like: {
            tpl: `<button class="f-button" data-social-action="like" title="Like">
                    <span class="like-heart">${HEART_SVG}</span>
                    <span class="like-count"></span>
                  </button>`,
            click: (_carousel: CarouselInstance, _event: Event) => {
              const content = activeContentItems.value[currentIndexRef.value]
              if (content?.id && content.reactions) {
                if (likedByMe.has(content.id)) {
                  likedByMe.delete(content.id)
                  content.reactions.likeCount--
                } else {
                  likedByMe.add(content.id)
                  content.reactions.likeCount++
                }
                const fancybox = Fancybox.getInstance()
                if (fancybox) {
                  updateToolbarState(fancybox)
                }
              }
            },
          },
          comment: {
            tpl: `<button class="f-button" data-social-action="comment" title="Comments">
                    <span class="comment-icon">${COMMENT_SVG}</span>
                    <span class="comment-count"></span>
                  </button>`,
            click: (_carousel: CarouselInstance, _event: Event) => {
              const contentId = currentContentId.value
              if (contentId) {
                commentModalRef.value?.open(contentId)
              }
            },
          },
        },
      },
      Thumbs: {
        showOnStart: true,
      },
    },
  })
}

function updateToolbarState(fancybox: FancyboxInstance) {
  const content = activeContentItems.value[currentIndexRef.value]
  if (!content?.id) {
    return
  }

  const container = fancybox.getContainer()

  const likeBtn = container.querySelector<HTMLElement>('[data-social-action="like"]')
  if (likeBtn) {
    const isLiked = likedByMe.has(content.id!)
    const count = content.reactions?.likeCount ?? 0
    likeBtn.classList.toggle('is-liked', isLiked)
    const likeCount = likeBtn.querySelector<HTMLElement>('.like-count')
    if (likeCount) {
      likeCount.textContent = count > 0 ? String(count) : ''
    }
  }

  const commentBtn = container.querySelector<HTMLElement>('[data-social-action="comment"]')
  if (commentBtn) {
    const count = commentStore.getCommentCount(content.id)
    const commentCount = commentBtn.querySelector<HTMLElement>('.comment-count')
    if (commentCount) {
      commentCount.textContent = count > 0 ? String(count) : ''
    }
  }
}

function refreshToolbarState() {
  const fancybox = Fancybox.getInstance()
  if (fancybox) {
    updateToolbarState(fancybox)
  }
}

function close() {
  Fancybox.close()
}
</script>
